import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLiveRefresh } from '../hooks/useLiveRefresh';
import ScensobConnectLogo from '../components/brand/ScensobConnectLogo';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { useWebSocket } from '../contexts/WebSocketContext';
import { apiCall } from '../utils/api';
import { compressImage } from '../utils/imageCompress';
import { toast } from '../components/ui/sonner';
import { 
  Plus, Briefcase, Users, User, DollarSign, Settings, LogOut, CheckCircle, 
  XCircle, Clock, TrendingUp, Calendar, MapPin, ChevronRight, X, Handshake,
  Building2, CreditCard, BarChart3, RefreshCw, Eye, MessageSquare,
  Shield, Target, Activity, FileCheck, Wallet, Heart, Sparkles, AlertCircle, Home,
  Bot, Send, HelpCircle, Headphones, Wand2, Pill, ClipboardList, Lightbulb,
  Phone, Mail, FileText, ChevronLeft, ArrowLeftRight,
  ChevronDown, Award, Search, Camera, Type, Pen, GraduationCap, Archive, ArchiveRestore, Trash2, UserPlus, Upload, BookOpen
} from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import RoleSwitcher from '../components/RoleSwitcher';
import SupportRequestPrompt from '../components/shared/SupportRequestPrompt';
import ActiveSupportBanner from '../components/shared/ActiveSupportBanner';
import PasswordConfirmModal from '../components/PasswordConfirmModal';
import BulkWorkerUpload from '../components/BulkWorkerUpload';
import RealTimeChat from '../components/RealTimeChat';
import MessagingInbox from '../components/MessagingInbox';
// Import Employer Portal Components (Phase 1-7)
import OrganizationSetup from '../components/employer/OrganizationSetup';
import AgencySetup from '../components/agent/AgencySetup';
import EnhancedJobPosting from '../components/employer/EnhancedJobPosting';
import WorkerMatching from '../components/employer/WorkerMatching';
import useUnreadMessages from '../hooks/useUnreadMessages';
import WorkforcePoliciesCard from '../components/org/WorkforcePoliciesCard';
import ShiftToolsButtons from '../components/org/ShiftToolsButtons';
import EmployerAiSupportChat from '../components/employer/EmployerAiSupportChat';
import ProjectTracker from '../components/employer/ProjectTracker';
import CompletionApproval from '../components/employer/CompletionApproval';
import PaymentManagement from '../components/employer/PaymentManagement';
import RelationshipManagement from '../components/employer/RelationshipManagement';
// Import Group Homes Enterprise Management (Phase 8)
import GroupHomesManagement from '../components/employer/GroupHomesManagement';
import AgentPortalPanel, { AgentLocationsTab } from '../components/agent/AgentPortalPanel';
import SensitiveSection from '../components/security/SensitiveSection';
import SupportHistoryPanel from '../components/shared/SupportHistoryPanel';
// Dashboard cleanup (Jul 2026): heavy tab panels are code-split with
// React.lazy so the initial employer/agency bundle stays lean. Each loads
// on first visit to its tab only.
const UserManagementPanel = lazy(() => import('../components/UserManagementPanel'));
const ClaimShifts = lazy(() => import('../components/agent/ClaimShifts'));
const AgencyContracts = lazy(() => import('../components/agent/AgencyContracts'));
const EmployerAnalyticsDashboard = lazy(() => import('../components/EmployerAnalyticsDashboard'));
const EmployerWorkerDirectory = lazy(() => import('../components/employer/EmployerWorkerDirectory'));
const SentInvitations = lazy(() => import('../components/employer/SentInvitations'));
const EmployerInbox = lazy(() => import('../components/employer/EmployerInbox'));
const GeofenceSettings = lazy(() => import('../components/employer/GeofenceSettings'));
const BulkAssignPanel = lazy(() => import('../components/org/BulkAssignPanel'));
const AcceptedJobsPanel = lazy(() => import('../components/employer/AcceptedJobsPanel'));
const CRMPanel = lazy(() => import('../components/employer/CRMPanel'));
const ScheduleCalendar = lazy(() => import('../components/employer/ScheduleCalendar'));
const LiveWorkforceDashboard = lazy(() => import('../components/employer/LiveWorkforceDashboard'));
const AutomationRulesPanel = lazy(() => import('../components/employer/AutomationRulesPanel'));
const CommunicationHub = lazy(() => import('../components/employer/CommunicationHub'));
const PaymentsFinancial = lazy(() => import('../components/employer/PaymentsFinancial'));
const BillingDashboard = lazy(() => import('../components/employer/BillingDashboard'));
const CompliancePanel = lazy(() => import('../components/employer/CompliancePanel'));
const BackgroundChecks = lazy(() => import('../components/admin/BackgroundChecks'));
const ShiftSwapManagement = lazy(() => import('../components/employer/ShiftSwapManagement'));
const TimesheetsPanel = lazy(() => import('../components/employer/TimesheetsPanel'));
const OrgHrisConsole = lazy(() => import('../components/hris/OrgHrisConsole'));
const PredictiveForecast = lazy(() => import('../components/employer/PredictiveForecast'));
const AutoPayrollSettings = lazy(() => import('../components/employer/AutoPayrollSettings'));
const LocationsPanel = lazy(() => import('../components/employer/LocationsPanel'));
const LocationsManager = lazy(() => import('../components/employer/LocationsManager'));
const ReportRecipientsManager = lazy(() => import('../components/ReportRecipientsManager'));
const NotificationPrefsCard = lazy(() => import('../components/shared/NotificationPrefsCard'));
const EmployerTaskReviewsSection = lazy(() => import('../components/employer/EmployerTaskReviewsSection'));
const EmployerWorkersOverviewSection = lazy(() => import('../components/employer/EmployerWorkersOverviewSection'));
const EmployerMyJobsSection = lazy(() => import('../components/employer/EmployerMyJobsSection'));
const EmployerSettingsSection = lazy(() => import('../components/employer/EmployerSettingsSection'));
const EmployerProfileOverview = lazy(() => import('../components/employer/EmployerProfileOverview'));

// iter173: platform-wide pay-rate editing gate
import PayRateGate from '../components/shared/PayRateGate';
const SecurityCenter = lazy(() => import('../components/security/SecurityCenter'));
const EmployerOrgPanel = lazy(() => import('../components/employer/EmployerOrgPanel'));
const HelpTraining = lazy(() => import('../components/shared/HelpTraining'));
const OnboardingCard = lazy(() => import('../components/shared/OnboardingCard'));
import EmployerTimeEditRequestsModal from '../components/employer/EmployerTimeEditRequestsModal';
import EmployerHelpSupportModal from '../components/employer/EmployerHelpSupportModal';
import EmployerShiftRequestsModal from '../components/employer/EmployerShiftRequestsModal';
import EmployerTopUpModal from '../components/employer/EmployerTopUpModal';
import EmployerJobFormModal from '../components/employer/EmployerJobFormModal';

export default function EmployerDashboard() {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const { isConnected } = useWebSocket();
  const fileInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    // Returning from a Stripe checkout — land on Payments so the
    // confirmation poll runs immediately and the status flips without a manual refresh.
    const params = new URLSearchParams(window.location.search);
    return (params.get('stripe_session_id') || params.get('stripe_cancelled')) ? 'payments' : 'my-jobs';
  });
  const [commsView, setCommsView] = useState('messages');
  const { unread: unreadMessages } = useUnreadMessages();
  const [jobs, setJobs] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showJobForm, setShowJobForm] = useState(false);
  const [showTopUpForm, setShowTopUpForm] = useState(false);
  const [showMessaging, setShowMessaging] = useState(false);
  const [messagingRecipient, setMessagingRecipient] = useState(null);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [showWorkersDropdown, setShowWorkersDropdown] = useState(false);
  const [showBulkWorkerUpload, setShowBulkWorkerUpload] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  
  // Employer Role State (Company Admin)
  const [employerRole, setEmployerRole] = useState(user?.employer_role || 'company_admin');
  
  // Employer Portal Feature States (Phase 1-7)
  const [showOrganizationSetup, setShowOrganizationSetup] = useState(false);
  const [showEnhancedJobPosting, setShowEnhancedJobPosting] = useState(false);
  const [showWorkerMatching, setShowWorkerMatching] = useState(false);
  const [showProjectTracker, setShowProjectTracker] = useState(false);
  const [showCompletionApproval, setShowCompletionApproval] = useState(false);
  const [showPaymentManagement, setShowPaymentManagement] = useState(false);
  const [showRelationshipManagement, setShowRelationshipManagement] = useState(false);
  const [showGroupHomesManagement, setShowGroupHomesManagement] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [showAiSupport, setShowAiSupport] = useState(false);
  const [showHelpSupport, setShowHelpSupport] = useState(false);
  const [helpView, setHelpView] = useState('main');
  const [showTopUp, setShowTopUp] = useState(false);
  
  // Task Reviews State
  const [taskReviews, setTaskReviews] = useState([]);
  const [taskReviewsLoading, setTaskReviewsLoading] = useState(false);
  const [reviewFilter, setReviewFilter] = useState('pending');
  
  // Shift Requests State
  const [shiftRequests, setShiftRequests] = useState([]);
  const [showShiftRequests, setShowShiftRequests] = useState(false);
  const [shiftRequestsLoading, setShiftRequestsLoading] = useState(false);
  
  // Time Edit Requests State
  const [timeEditRequests, setTimeEditRequests] = useState([]);
  const [showTimeEditRequests, setShowTimeEditRequests] = useState(false);
  const [timeEditRequestsLoading, setTimeEditRequestsLoading] = useState(false);

  // Job archive/delete state
  const [jobActionModal, setJobActionModal] = useState(null); // { mode: 'archive'|'delete'|'bulk-archive'|'bulk-delete', job?, jobIds? }
  const [jobActionBusy, setJobActionBusy] = useState(false);
  const [jobActionError, setJobActionError] = useState('');
  const [showArchivedJobs, setShowArchivedJobs] = useState(false);
  const [selectedJobIds, setSelectedJobIds] = useState(() => new Set());
  const [recentlyArchived, setRecentlyArchived] = useState([]);
  const [showRecentlyArchived, setShowRecentlyArchived] = useState(true);
  const [restoringRecent, setRestoringRecent] = useState(false);

  // iter79: My Jobs sub-view + search filter + drafts list.
  const [myJobsView, setMyJobsView] = useState('posted'); // 'posted' | 'drafts'
  const [jobsSearchQuery, setJobsSearchQuery] = useState('');
  const [drafts, setDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  
  // Handle Logout
  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    // Use window.location for a full page refresh to ensure all state is cleared
    window.location.href = '/';
  };
  
  const [jobForm, setJobForm] = useState({
    title: '',
    description: '',
    category: 'onsite',
    pay_rate: '',
    start_time: '',
    end_time: '',
    location: {
      lat: 37.7749,
      lng: -122.4194,
      address: '',
      radius_meters: 100,
    },
    required_skills: '',
    requirements: '',
    requires_medication: false,
    expected_tasks: [],
    allow_photo_uploads: false,
    task_verification: {
      manager_name: '',
      manager_signature: '',
      requirement_accepted: null,
      verified_at: null
    }
  });
  
  // AI and enhanced job posting states
  const [aiJobLoading, setAiJobLoading] = useState(false);
  const [newJobTask, setNewJobTask] = useState({ name: '', is_medication: false });
  
  // E-Signature states for basic job form
  const [basicSignatureMode, setBasicSignatureMode] = useState('type');
  const [basicTypedSignature, setBasicTypedSignature] = useState('');
  const basicCanvasRef = React.useRef(null);
  const [isBasicDrawing, setIsBasicDrawing] = useState(false);
  const [basicLastPos, setBasicLastPos] = useState({ x: 0, y: 0 });
  
  // Canvas drawing functions for basic job form
  const getBasicCanvasCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const startBasicDrawing = (e) => {
    const canvas = basicCanvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    const { x, y } = getBasicCanvasCoordinates(e, canvas);
    setIsBasicDrawing(true);
    setBasicLastPos({ x, y });
  };

  const drawBasic = (e) => {
    if (!isBasicDrawing) return;
    const canvas = basicCanvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    const ctx = canvas.getContext('2d');
    const { x, y } = getBasicCanvasCoordinates(e, canvas);
    ctx.beginPath();
    ctx.moveTo(basicLastPos.x, basicLastPos.y);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1e40af';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setBasicLastPos({ x, y });
  };

  const stopBasicDrawing = () => {
    if (isBasicDrawing && basicCanvasRef.current) {
      const signatureData = basicCanvasRef.current.toDataURL();
      setJobForm(prev => ({
        ...prev,
        task_verification: { ...prev.task_verification, manager_signature: signatureData, requirement_accepted: null }
      }));
    }
    setIsBasicDrawing(false);
  };

  const clearBasicCanvas = () => {
    const canvas = basicCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setJobForm(prev => ({
      ...prev,
      task_verification: { ...prev.task_verification, manager_signature: '', requirement_accepted: null }
    }));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showWorkersDropdown && !e.target.closest('[data-testid="employer-nav-workers"]')) {
        setShowWorkersDropdown(false);
      }
      if (showProfileDropdown && !e.target.closest('[data-testid="employer-profile-dropdown"]')) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showWorkersDropdown]);

  // Resolve a profile photo path into an absolute URL the <img> tag can load.
  // Handles relative paths from the backend (/api/uploads/profiles/xxx.jpg) as
  // well as already-absolute URLs.
  const getProfilePhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    if (photo.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${photo}`;
    if (photo.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}/api${photo}`;
    return photo;
  };

  // Upload a new profile photo, refresh the in-memory user so the avatars
  // update without a hard reload. Accepts the file input change event.
  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose a JPEG, PNG, or WebP image');
      e.target.value = '';
      return;
    }
    setUploadingPhoto(true);
    try {
      // Downscale/compress so large phone photos always fit the upload cap.
      let toSend = file;
      try {
        toSend = await compressImage(file);
      } catch (_err) {
        if (file.size > 5 * 1024 * 1024) {
          throw new Error('This image format could not be processed — please use a JPG or PNG under 5 MB');
        }
      }
      const form = new FormData();
      form.append('file', toSend);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employer/profile/photo`, {
        method: 'POST',
        // iter77 Phase B: httpOnly access_token cookie carries auth; no
        // manual Bearer header needed. See /app/backend/auth_cookie_utils.py
        credentials: 'include',
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Upload failed');
      updateUser({ ...user, profile_photo: data.file_url, profile_image: data.file_url });
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(err.message || 'Could not upload photo');
    } finally {
      setUploadingPhoto(false);
      if (e.target) e.target.value = '';
    }
  };

  useEffect(() => {
    loadJobs();
    loadWallet();
    loadVerificationStatus();
    const appEl = document.querySelector('.App');
    const prevAppBg = appEl?.style.background;
    const prevBodyBg = document.body.style.background;
    if (appEl) appEl.style.background = '#f8fafc';
    document.body.style.background = '#f8fafc';
    return () => {
      if (appEl) appEl.style.background = prevAppBg || '';
      document.body.style.background = prevBodyBg || '';
    };
  }, []);  

  // Reload jobs when archive toggle changes
  useEffect(() => {
    loadJobs();
     
  }, [showArchivedJobs]);

  // Live sync: main jobs board + wallet update on any relevant change by anyone.
  useLiveRefresh(['jobs', 'shifts', 'applications', 'payments', 'payroll', 'wallet', 'attendance'], () => {
    loadJobs();
    loadWallet();
  });

  useEffect(() => {
    // Set employer role from user data
    if (user?.employer_role) {
      setEmployerRole(user.employer_role);
    }
  }, []);

  const loadVerificationStatus = async () => {
    try {
      // Agents have their own dedicated verification flow (/api/agent/*) so we
      // must call the correct endpoint based on role — otherwise an approved
      // agent will see the legacy "Complete Your Business Verification" banner
      // forever because the employer endpoint returns 403 for them.
      const isAgent = user?.role === 'agent';
      const endpoint = isAgent
        ? '/api/agent/verification/status'
        : '/api/employer/verification/status';
      const response = await apiCall(endpoint);
      // For agents we also flag the response so the banner can be hidden once
      // their user.verification_status is already 'approved' (since the legacy
      // `verification` document may still say 'pending' even after the new
      // admin Profile Verification approve flow has unblocked them).
      const isUnifiedApproved = user?.verification_status === 'approved' || user?.is_admin_verified === true;
      let v = response.verification || { status: 'not_started' };
      if (isUnifiedApproved) {
        v = { ...v, status: 'verified' };
      }
      setVerificationStatus(v);
    } catch (error) {
      console.error('Failed to load verification status:', error);
    }
  };

  const loadJobs = async () => {
    setLoading(true);
    try {
      const status = showArchivedJobs
        ? 'active,assigned,in_progress,pending_approval,completed,cancelled,archived'
        : 'active,assigned,in_progress,pending_approval,completed,cancelled';
      const response = await apiCall(`/api/jobs/search?status=${encodeURIComponent(status)}&limit=200`);
      const myJobs = (response.jobs || []).filter((job) => job.employer_id === user._id);
      setJobs(myJobs);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
    // Refresh the recently-archived banner whenever we reload the job list
    loadRecentlyArchived();
  };

  // iter79: fetch saved drafts for the "Drafts" sub-tab.
  const loadDrafts = async () => {
    setDraftsLoading(true);
    try {
      const res = await apiCall('/api/jobs/drafts');
      setDrafts(res.drafts || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load drafts');
      setDrafts([]);
    } finally {
      setDraftsLoading(false);
    }
  };

  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm('Delete this draft? This action cannot be undone.')) return;
    try {
      await apiCall(`/api/jobs/drafts/${draftId}`, { method: 'DELETE' });
      toast.success('Draft deleted');
      setDrafts((prev) => prev.filter((d) => d._id !== draftId));
    } catch (error) {
      toast.error(error.message || 'Failed to delete draft');
    }
  };

  useEffect(() => {
    if (activeTab === 'my-jobs' && myJobsView === 'drafts') {
      loadDrafts();
    }
     
  }, [activeTab, myJobsView]);

  // ---- Recently archived (last 24h) quick-restore banner ----
  const loadRecentlyArchived = async () => {
    try {
      const res = await apiCall(`/api/jobs/search?status=archived&limit=200`);
      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      const uid = user._id;
      const mine = (res.jobs || []).filter((j) => {
        // Match any owner field — covers employers, agents, and sub-agents
        // inside an organization where the job may be tied to a company_id or
        // posted_by instead of the personal user id.
        const owns = j.employer_id === uid || j.posted_by === uid || j.company_id === uid;
        return owns && j.archived_at && new Date(j.archived_at).getTime() >= cutoff;
      });
      mine.sort((a, b) => new Date(b.archived_at).getTime() - new Date(a.archived_at).getTime());
      setRecentlyArchived(mine);
    } catch {
      setRecentlyArchived([]);
    }
  };

  const restoreAllRecentlyArchived = async () => {
    const ids = recentlyArchived.map((j) => j._id);
    if (!ids.length) return;
    setRestoringRecent(true);
    try {
      const res = await apiCall('/api/jobs/bulk-restore', {
        method: 'POST',
        body: JSON.stringify({ job_ids: ids }),
      });
      toast.success(`Restored ${res.restored || 0} job${(res.restored || 0) !== 1 ? 's' : ''}`);
      await loadJobs();
    } catch (e) {
      toast.error(e?.message || 'Failed to restore');
    } finally {
      setRestoringRecent(false);
    }
  };

  const restoreOneRecentlyArchived = async (jobId) => {
    try {
      await apiCall(`/api/jobs/${jobId}/restore`, { method: 'POST' });
      toast.success('Job restored');
      await loadJobs();
    } catch (e) {
      toast.error(e?.message || 'Failed to restore job');
    }
  };

  // ---- Job archive / delete (single + bulk) ----
  const submitJobAction = async ({ password, reason }) => {
    if (!jobActionModal) return;
    setJobActionBusy(true);
    setJobActionError('');
    const { mode, job, jobIds } = jobActionModal;
    try {
      if (mode === 'archive') {
        await apiCall(`/api/jobs/${job._id}/archive`, {
          method: 'POST',
          body: JSON.stringify({ password, reason }),
        });
        toast.success('Job archived');
      } else if (mode === 'delete') {
        await apiCall(`/api/jobs/${job._id}`, {
          method: 'DELETE',
          body: JSON.stringify({ password, reason }),
        });
        toast.success('Job permanently deleted');
      } else if (mode === 'bulk-archive') {
        const res = await apiCall('/api/jobs/bulk-archive', {
          method: 'POST',
          body: JSON.stringify({ job_ids: jobIds, password, reason }),
        });
        const skipped = (res.skipped || []).length;
        toast.success(`Archived ${res.archived || 0} job${(res.archived||0)!==1?'s':''}${skipped ? ` (${skipped} skipped)` : ''}`);
        setSelectedJobIds(new Set());
      } else if (mode === 'bulk-delete') {
        const res = await apiCall('/api/jobs/bulk-delete', {
          method: 'POST',
          body: JSON.stringify({ job_ids: jobIds, password, reason }),
        });
        const skipped = (res.skipped || []).length;
        toast.success(`Deleted ${res.deleted || 0} job${(res.deleted||0)!==1?'s':''}${skipped ? ` (${skipped} skipped)` : ''}`);
        setSelectedJobIds(new Set());
      }
      setJobActionModal(null);
      await loadJobs();
    } catch (e) {
      setJobActionError(e?.message || 'Action failed');
    } finally {
      setJobActionBusy(false);
    }
  };

  const restoreJob = async (job) => {
    try {
      await apiCall(`/api/jobs/${job._id}/restore`, { method: 'POST' });
      toast.success('Job restored');
      await loadJobs();
    } catch (e) {
      toast.error(e?.message || 'Failed to restore job');
    }
  };

  const bulkRestoreJobs = async () => {
    const ids = Array.from(selectedJobIds);
    if (!ids.length) return;
    try {
      const res = await apiCall('/api/jobs/bulk-restore', {
        method: 'POST',
        body: JSON.stringify({ job_ids: ids }),
      });
      const skipped = (res.skipped || []).length;
      toast.success(`Restored ${res.restored || 0} job${(res.restored||0)!==1?'s':''}${skipped ? ` (${skipped} skipped)` : ''}`);
      setSelectedJobIds(new Set());
      await loadJobs();
    } catch (e) {
      toast.error(e?.message || 'Failed to restore');
    }
  };

  const toggleJobSelected = (jobId) => {
    setSelectedJobIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const clearJobSelection = () => setSelectedJobIds(new Set());

  const toggleSelectAllJobs = () => {
    const visibleIds = jobs.map((j) => j._id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedJobIds.has(id));
    if (allSelected) setSelectedJobIds(new Set());
    else setSelectedJobIds(new Set(visibleIds));
  };

  const loadWallet = async () => {
    try {
      const response = await apiCall('/api/wallet');
      if (response.wallet) {
        setWallet(response.wallet);
      } else {
        setWallet({ balance: response.balance || 0, transactions: [] });
      }
    } catch (error) {
      console.error('Failed to load wallet:', error);
      setWallet({ balance: 0, transactions: [] });
    }
  };

  // Load task reviews for approval
  const loadTaskReviews = async () => {
    setTaskReviewsLoading(true);
    try {
      const response = await apiCall(`/api/employer/task-reviews?status=${reviewFilter}`);
      setTaskReviews(response.reviews || []);
    } catch (error) {
      console.error('Failed to load task reviews:', error);
      setTaskReviews([]);
    } finally {
      setTaskReviewsLoading(false);
    }
  };

  // Approve task review
  const handleApproveTask = async (jobId) => {
    try {
      await apiCall(`/api/employer/task-reviews/${jobId}/approve`, {
        method: 'POST',
        body: JSON.stringify({ notes: '' })
      });
      toast.success('Tasks approved successfully!');
      loadTaskReviews();
    } catch (error) {
      toast.error(error.message || 'Failed to approve tasks');
    }
  };

  // Reject task review
  const handleRejectTask = async (jobId, reason) => {
    try {
      await apiCall(`/api/employer/task-reviews/${jobId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason || 'Tasks need to be redone' })
      });
      toast.success('Tasks rejected. Worker has been notified.');
      loadTaskReviews();
    } catch (error) {
      toast.error(error.message || 'Failed to reject tasks');
    }
  };

  // Load shift requests
  const loadShiftRequests = async () => {
    setShiftRequestsLoading(true);
    try {
      const response = await apiCall('/api/employer/shift-requests');
      setShiftRequests(response.requests || []);
    } catch (error) {
      console.error('Failed to load shift requests:', error);
      setShiftRequests([]);
    } finally {
      setShiftRequestsLoading(false);
    }
  };

  // Approve shift request
  const handleApproveShiftRequest = async (applicationId) => {
    try {
      await apiCall(`/api/employer/shift-requests/${applicationId}/approve`, {
        method: 'POST'
      });
      toast.success('Worker approved and assigned to shift!');
      loadShiftRequests();
      loadJobs();
    } catch (error) {
      toast.error(error.message || 'Failed to approve request');
    }
  };

  // Reject shift request
  const handleRejectShiftRequest = async (applicationId, reason) => {
    try {
      await apiCall(`/api/employer/shift-requests/${applicationId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason || 'Application not selected' })
      });
      toast.success('Request rejected');
      loadShiftRequests();
    } catch (error) {
      toast.error(error.message || 'Failed to reject request');
    }
  };

  // Load shift requests on mount and periodically
  useEffect(() => {
    loadShiftRequests();
    loadTimeEditRequests();
    const interval = setInterval(() => {
      loadShiftRequests();
      loadTimeEditRequests();
    }, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Load time edit requests
  const loadTimeEditRequests = async () => {
    setTimeEditRequestsLoading(true);
    try {
      const response = await apiCall('/api/employer/time-edit-requests');
      setTimeEditRequests(response.requests || []);
    } catch (error) {
      console.error('Failed to load time edit requests:', error);
      setTimeEditRequests([]);
    } finally {
      setTimeEditRequestsLoading(false);
    }
  };

  // Handle time edit request approval
  const handleApproveTimeEdit = async (requestId) => {
    try {
      await apiCall(`/api/employer/time-edit-requests/${requestId}`, {
        method: 'PUT',
        body: JSON.stringify({ action: 'approve' })
      });
      toast.success('Time edit request approved');
      loadTimeEditRequests();
    } catch (error) {
      toast.error(error.message || 'Failed to approve request');
    }
  };

  // Handle time edit request rejection
  const handleRejectTimeEdit = async (requestId, reason) => {
    try {
      await apiCall(`/api/employer/time-edit-requests/${requestId}`, {
        method: 'PUT',
        body: JSON.stringify({ action: 'reject', rejection_reason: reason || 'Request denied' })
      });
      toast.success('Time edit request rejected');
      loadTimeEditRequests();
    } catch (error) {
      toast.error(error.message || 'Failed to reject request');
    }
  };

  // Load task reviews when tab changes or filter changes
  useEffect(() => {
    if (activeTab === 'task-reviews') {
      loadTaskReviews();
    }
  }, [activeTab, reviewFilter]);

  const handleTopUp = async (e) => {
    if (e) e.preventDefault();
    const amount = parseFloat(topUpAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      const res = await apiCall('/api/wallet/topup/stripe', {
        method: 'POST',
        body: JSON.stringify({
          amount,
          origin_url: window.location.origin,
          return_path: window.location.pathname || '/employer',
        }),
      });
      if (res.url) {
        toast.info('Redirecting to secure Stripe checkout…');
        window.location.href = res.url;
        return;
      }
      toast.error('Could not start checkout');
    } catch (error) {
      toast.error(error.message || 'Failed to start card payment');
    }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    
    try {
      const skillsArray = jobForm.required_skills
        ? jobForm.required_skills.split(',').map((s) => s.trim())
        : [];

      // Convert expected_tasks to shift_tasks format for backend
      const shiftTasks = jobForm.expected_tasks.map((task, idx) => ({
        id: idx + 1,
        text: task.name,
        completed: false,
        is_medication: task.is_medication || false
      }));

      await apiCall('/api/jobs', {
        method: 'POST',
        body: JSON.stringify({
          ...jobForm,
          pay_rate: jobForm.pay_rate ? parseFloat(jobForm.pay_rate) : null,
          start_time: new Date(jobForm.start_time).toISOString(),
          end_time: new Date(jobForm.end_time).toISOString(),
          required_skills: skillsArray,
          requires_medication: jobForm.requires_medication,
          expected_tasks: jobForm.expected_tasks,
          shift_tasks: shiftTasks,
          allow_photo_uploads: jobForm.allow_photo_uploads,
          task_verification: jobForm.task_verification,
        }),
      });

      toast.success('Job posted successfully!');
      setShowJobForm(false);
      setJobForm({
        title: '',
        description: '',
        category: 'onsite',
        pay_rate: '',
        start_time: '',
        end_time: '',
        location: { lat: 37.7749, lng: -122.4194, address: '', radius_meters: 100 },
        required_skills: '',
        requirements: '',
        requires_medication: false,
        expected_tasks: [],
        allow_photo_uploads: false,
        task_verification: {
          manager_name: '',
          manager_signature: '',
          requirement_accepted: null,
          verified_at: null
        }
      });
      setNewJobTask({ name: '', is_medication: false });
      setBasicTypedSignature('');
      loadJobs();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // AI: Generate job description
  const generateJobDescription = async () => {
    if (!jobForm.title) {
      toast.error('Please enter a job title first');
      return;
    }
    setAiJobLoading(true);
    try {
      const response = await apiCall('/api/pm/ai/generate-shift-notes', {
        method: 'POST',
        body: JSON.stringify({
          shift_type: jobForm.title,
          location_name: jobForm.location.address || 'Workplace',
          duration: jobForm.start_time && jobForm.end_time ? 
            `${Math.round((new Date(jobForm.end_time) - new Date(jobForm.start_time)) / 3600000)} hours` : '8 hours',
          has_medication: jobForm.requires_medication,
          existing_tasks: jobForm.expected_tasks.map(t => t.name),
          tone: 'professional'
        })
      });
      if (response.success && response.generated_notes) {
        setJobForm(prev => ({ ...prev, description: response.generated_notes }));
        toast.success('Description generated!');
      } else if (response.timed_out) {
        // iter77: backend now returns a clean JSON error before Cloudflare's
        // 100s edge timeout fires. Surface the friendlier message instead of
        // the generic "Failed to generate description".
        toast.error(response.error || 'AI took too long — please try again or write it manually.');
      } else if (response.error) {
        toast.error(response.error);
      }
    } catch (error) {
      // Network/HTML fallback — this used to include the raw Cloudflare 504
      // HTML page. Show a user-friendly message instead.
      const msg = (error?.message || '').includes('Unexpected token') || (error?.message || '').includes('504')
        ? 'AI service is temporarily slow. Please try again in a moment.'
        : 'Failed to generate description';
      toast.error(msg);
    } finally {
      setAiJobLoading(false);
    }
  };

  // AI: Suggest tasks for job
  const suggestJobTasks = async () => {
    setAiJobLoading(true);
    try {
      const hour = jobForm.start_time ? new Date(jobForm.start_time).getHours() : 9;
      const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : hour < 21 ? 'evening' : 'overnight';
      
      const response = await apiCall('/api/pm/ai/suggest-tasks', {
        method: 'POST',
        body: JSON.stringify({
          shift_type: jobForm.title || 'general care',
          time_of_day: timeOfDay,
          location_type: 'workplace',
          has_medication: jobForm.requires_medication,
          existing_tasks: jobForm.expected_tasks.map(t => t.name)
        })
      });
      
      if (response.success && response.suggested_tasks?.length > 0) {
        const newTasks = response.suggested_tasks
          .filter(t => !jobForm.expected_tasks.some(et => et.name.toLowerCase() === t.name.toLowerCase()))
          .map(t => ({ name: t.name, is_medication: t.is_medication || false }));
        
        setJobForm(prev => ({
          ...prev,
          expected_tasks: [...prev.expected_tasks, ...newTasks]
        }));
        toast.success(`Added ${newTasks.length} suggested tasks!`);
      } else {
        toast.info('No new task suggestions available');
      }
    } catch (error) {
      toast.error('Failed to get task suggestions');
    } finally {
      setAiJobLoading(false);
    }
  };

  // Add expected task
  const addJobTask = () => {
    if (newJobTask.name.trim()) {
      setJobForm(prev => ({
        ...prev,
        expected_tasks: [...prev.expected_tasks, { ...newJobTask, name: newJobTask.name.trim() }]
      }));
      setNewJobTask({ name: '', is_medication: false });
    }
  };

  // Remove expected task
  const removeJobTask = (index) => {
    setJobForm(prev => ({
      ...prev,
      expected_tasks: prev.expected_tasks.filter((_, i) => i !== index)
    }));
  };

  // Handle medication toggle
  const handleJobMedicationToggle = (checked) => {
    setJobForm(prev => ({ ...prev, requires_medication: checked }));
    if (checked && !jobForm.expected_tasks.some(t => t.is_medication)) {
      setJobForm(prev => ({
        ...prev,
        requires_medication: checked,
        expected_tasks: [...prev.expected_tasks, { name: 'Administer Medication', is_medication: true }]
      }));
    }
  };

  const handleConfirmWorker = async (jobId, workerId) => {
    try {
      await apiCall(`/api/jobs/${jobId}/confirm?worker_id=${workerId}`, { method: 'POST' });
      toast.success('Worker confirmed! Payment held in escrow.');
      loadJobs();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleReleasePayment = async (jobId) => {
    try {
      await apiCall(`/api/payments/release/${jobId}`, { method: 'POST' });
      toast.success('Payment released successfully!');
      loadJobs();
      loadWallet();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getJobStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: <Eye className="w-3 h-3" /> },
      assigned: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: <Users className="w-3 h-3" /> },
      in_progress: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', icon: <Clock className="w-3 h-3" /> },
      pending_approval: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', icon: <CheckCircle className="w-3 h-3" /> },
      completed: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: <CheckCircle className="w-3 h-3" /> },
      cancelled: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', icon: <XCircle className="w-3 h-3" /> },
      archived: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: <Archive className="w-3 h-3" /> },
    };
    const config = statusConfig[status] || statusConfig.active;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${config.bg} ${config.text} border ${config.border}`}>
        {config.icon}
        {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </span>
    );
  };

  const tabs = [
    { id: 'my-jobs', label: 'My Jobs', icon: <Briefcase className="w-4 h-4" /> },
    { id: 'schedule', label: 'Calendar', icon: <Calendar className="w-4 h-4" /> },
    { id: 'bulk-assign', label: 'Bulk Assign', icon: <Users className="w-4 h-4" /> },
    { id: 'workers', label: 'Workers', icon: <Users className="w-4 h-4" /> },
    { id: 'crm', label: 'CRM', icon: <Heart className="w-4 h-4" /> },
    { id: 'payments', label: 'Payments', icon: <Wallet className="w-4 h-4" /> },
    { id: 'billing', label: 'Billing', icon: <FileText className="w-4 h-4" /> },
    { id: 'analytics', label: 'Analytics', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'comms', label: 'Comms', icon: <MessageSquare className="w-4 h-4" /> },
    { id: 'shift-swaps', label: 'Swaps', icon: <ArrowLeftRight className="w-4 h-4" /> },
    { id: 'contracts', label: 'Contracts', icon: <Handshake className="w-4 h-4" /> },
    { id: 'hris', label: 'HR (HRIS)', icon: <GraduationCap className="w-4 h-4" /> },
    { id: 'timesheets', label: 'Timesheets', icon: <Clock className="w-4 h-4" /> },
    { id: 'compliance', label: 'Compliance', icon: <Shield className="w-4 h-4" /> },
    { id: 'background-checks', label: 'Bg Checks', icon: <Shield className="w-4 h-4" /> },
    { id: 'help-training', label: 'Help & Training', icon: <BookOpen className="w-4 h-4" /> },
    { id: 'settings', label: 'Settings', icon: <Settings className="w-4 h-4" /> },
  ];

  // Stats
  const activeJobs = jobs.filter(j => j.status === 'active').length;
  const completedJobs = jobs.filter(j => j.status === 'completed').length;
  const totalSpent = jobs.filter(j => j.status === 'completed').reduce((sum, j) => sum + (j.total_pay || 0), 0);

  return (
    <div className="min-h-screen w-full bg-slate-50 text-slate-900">
      {/* Support-mode request prompt — visible only when a platform admin
          is waiting for consent to enter this org (iter163). */}
      <SupportRequestPrompt />
      {/* Active-support banner — sticky red banner + countdown + Revoke
          shown when a platform admin currently has an active session
          inside this org (iter164). */}
      <ActiveSupportBanner />
      {/* First-login training card — self-dismisses once seen */}
      <Suspense fallback={null}><OnboardingCard role={user?.role === 'agent' ? 'agent' : 'employer'} /></Suspense>
      {/* Header */}
      <header className={`${user?.role === 'agent' ? 'agent-header-glass' : 'employer-header-glass'} text-white sticky top-0 z-40`}>
        <div className={user?.role === 'agent' ? 'agent-header-aurora' : 'employer-header-aurora'} aria-hidden="true" />
        <div className="relative px-4 sm:px-6 lg:px-8">
          {/* Top Row: Logo, Balance, Actions, Profile */}
          <div className="flex flex-wrap justify-between items-center min-h-14 py-1 gap-y-1">
            {/* Logo */}
            <ScensobConnectLogo
              size={28}
              subtitle={user?.role === 'agent' ? 'Staffing Agency Portal' : 'Employer Portal'}
              className="flex-shrink-0"
            />

            {/* Right: Balance + Actions + Profile */}
            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-wrap justify-end">
              {wallet && (
                <div className="hidden sm:flex items-center">
                  <div className="bg-white/10 backdrop-blur-sm px-2.5 py-1 rounded-lg border border-white/20">
                    <p className="text-[10px] text-white/70 font-medium">Balance</p>
                    <p className="text-xs font-bold text-white tabular-nums" data-testid="employer-wallet-balance">
                      ${wallet.balance?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Messaging Button */}
              <button
                onClick={() => { setActiveTab('comms'); setShowMessaging(false); }}
                className="relative p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                title="Messages"
                data-testid="employer-messaging-button"
              >
                <MessageSquare className="w-4 h-4" />
                {unreadMessages > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center ring-2 ring-[#0f1419]" data-testid="employer-header-unread-badge">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </button>

              {/* AI Support Button */}
              <button
                onClick={() => setShowAiSupport(true)}
                className="flex items-center gap-1 px-2 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                title="AI Support"
                data-testid="employer-ai-support-btn"
              >
                <Bot className="w-4 h-4" />
                <span className="text-xs font-medium hidden lg:inline">Help</span>
              </button>

              {/* Shift Requests Button */}
              <button
                onClick={() => setShowShiftRequests(true)}
                className="relative flex items-center gap-1 px-2.5 py-1.5 bg-amber-500/90 hover:bg-amber-500 rounded-lg transition-colors shadow-sm"
                title="Shift Requests"
                data-testid="employer-shift-requests-btn"
              >
                <FileCheck className="w-4 h-4 text-white" />
                <span className="text-xs font-semibold text-white hidden lg:inline">Shifts</span>
                {shiftRequests.length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-sm">
                    {shiftRequests.length}
                  </span>
                )}
              </button>
              
              {/* Time Edit Requests Button */}
              <button
                onClick={() => setShowTimeEditRequests(true)}
                className="relative flex items-center gap-1 px-2.5 py-1.5 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors shadow-sm"
                title="Time Edit Requests"
                data-testid="employer-time-edit-requests-btn"
              >
                <Pen className="w-4 h-4 text-amber-700" />
                <span className="text-xs font-semibold text-amber-700 hidden lg:inline">Time Edits</span>
                {timeEditRequests.filter(r => r.status === 'pending').length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-sm">
                    {timeEditRequests.filter(r => r.status === 'pending').length}
                  </span>
                )}
              </button>
              
              <NotificationBell />
              <RoleSwitcher tone="dark" className="hidden lg:flex" />
              
              {/* Profile Section with Dropdown */}
              <div className="relative flex items-center gap-2 pl-3 ml-1 border-l border-white/20" data-testid="employer-profile-dropdown">
                <button
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  className="flex items-center gap-2 hover:bg-white/10 rounded-lg px-2 py-1 transition-colors"
                  data-testid="employer-profile-btn"
                >
                  <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 border-2 border-white/30 overflow-hidden">
                    {getProfilePhotoUrl(user?.profile_photo || user?.profile_image) ? (
                      <img
                        src={getProfilePhotoUrl(user?.profile_photo || user?.profile_image)}
                        alt={user?.full_name || 'Profile'}
                        className="w-full h-full object-cover"
                        data-testid="employer-profile-avatar-img"
                      />
                    ) : (
                      <span className="text-sm font-bold text-white">
                        {user?.full_name?.charAt(0)?.toUpperCase() || (user?.role === 'agent' ? 'A' : 'E')}
                      </span>
                    )}
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-semibold text-white leading-tight">{user?.full_name || (user?.role === 'agent' ? 'Agent' : 'Employer')}</p>
                    <p className="text-[10px] text-white/60">{user?.role === 'agent' ? 'Agent' : 'Employer'}</p>
                  </div>
                  <ChevronDown className={`w-3.5 h-3.5 text-white/60 transition-transform ${showProfileDropdown ? 'rotate-180' : ''}`} />
                </button>

                {/* Profile Dropdown */}
                {showProfileDropdown && (
                  <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50">
                    {/* Dropdown Header */}
                    <div className="bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 px-5 py-4 flex items-center gap-3">
                      <div className="relative group">
                        <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0 border-2 border-white/30 overflow-hidden">
                          {getProfilePhotoUrl(user?.profile_photo || user?.profile_image) ? (
                            <img
                              src={getProfilePhotoUrl(user?.profile_photo || user?.profile_image)}
                              alt={user?.full_name || 'Profile'}
                              className="w-full h-full object-cover"
                              data-testid="employer-profile-avatar-large"
                            />
                          ) : (
                            <span className="text-xl font-bold text-white">
                              {user?.full_name?.charAt(0)?.toUpperCase() || (user?.role === 'agent' ? 'A' : 'E')}
                            </span>
                          )}
                        </div>
                        {/* Camera overlay — click to upload */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                          disabled={uploadingPhoto}
                          className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white text-slate-900 border-2 border-white shadow-md flex items-center justify-center hover:scale-110 transition disabled:opacity-60"
                          title="Change profile photo"
                          data-testid="employer-profile-photo-change-btn"
                        >
                          {uploadingPhoto ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Camera className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleProfilePhotoUpload}
                          className="hidden"
                          data-testid="employer-profile-photo-input"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-bold text-white truncate">{user?.full_name || 'Employer'}</p>
                        <p className="text-xs text-white/70 truncate">{user?.email}</p>
                      </div>
                      <div className="px-2.5 py-1 bg-emerald-500/20 border border-emerald-400/40 rounded-lg flex-shrink-0">
                        <p className="text-[10px] font-bold text-emerald-300">Verified</p>
                        <p className="text-[10px] font-bold text-emerald-300">Profile</p>
                      </div>
                    </div>
                    {/* Dropdown Menu Items */}
                    <div className="py-2">
                      <RoleSwitcher variant="menu" className="px-2" onDone={() => setShowProfileDropdown(false)} />
                      <button
                        onClick={() => { setActiveTab('overview'); setShowProfileDropdown(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-slate-50 transition"
                        data-testid="profile-my-profile"
                      >
                        <User className="w-5 h-5 text-slate-500" />
                        <span className="text-sm font-medium text-slate-800">My Profile</span>
                      </button>
                      <button
                        onClick={() => { setActiveTab('payments'); setShowProfileDropdown(false); }}
                        className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-slate-50 transition"
                        data-testid="profile-wallet"
                      >
                        <Wallet className="w-5 h-5 text-slate-500" />
                        <span className="text-sm font-medium text-slate-800">Wallet & Earnings</span>
                      </button>
                      <button
                        onClick={() => { setActiveTab('settings'); setShowProfileDropdown(false); }}
                        className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-slate-50 transition"
                        data-testid="profile-settings"
                      >
                        <Settings className="w-5 h-5 text-slate-500" />
                        <span className="text-sm font-medium text-slate-800">Settings</span>
                      </button>
                      <button
                        onClick={() => { setShowHelpSupport(true); setHelpView('main'); setShowProfileDropdown(false); }}
                        className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-slate-50 transition"
                        data-testid="profile-help"
                      >
                        <HelpCircle className="w-5 h-5 text-slate-500" />
                        <span className="text-sm font-medium text-slate-800">Help & Support</span>
                      </button>
                    </div>
                    {/* Sign Out */}
                    <div className="border-t border-slate-100 py-2">
                      <button
                        onClick={() => { setShowProfileDropdown(false); handleLogout(); }}
                        className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-red-50 transition"
                        data-testid="profile-signout"
                      >
                        <LogOut className="w-5 h-5 text-red-500" />
                        <span className="text-sm font-medium text-red-600">Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Row: Navigation Tabs */}
          <nav className="hidden md:flex flex-wrap items-center gap-1 pb-2 pt-1 border-t border-white/10" data-testid="employer-main-nav">
            {tabs.map((tab) => (
              tab.id === 'workers' ? (
                <div key={tab.id} className="relative">
                  <button
                    onClick={() => setShowWorkersDropdown(!showWorkersDropdown)}
                    className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                      activeTab === tab.id || showWorkersDropdown
                        ? 'text-white shadow-lg shadow-indigo-500/40'
                        : 'text-white/75 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid={`employer-nav-${tab.id}`}
                  >
                    {(activeTab === tab.id || showWorkersDropdown) && (
                      <span className={user?.role === 'agent' ? 'nav-active-pill-agent' : 'nav-active-pill'} aria-hidden="true" />
                    )}
                    <span className="relative flex items-center gap-1.5">
                      {tab.icon}
                      {tab.label}
                      <ChevronDown className={`w-3 h-3 transition-transform ${showWorkersDropdown ? 'rotate-180' : ''}`} />
                    </span>
                  </button>
                    {showWorkersDropdown && (
                      <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-50 max-h-[70vh] overflow-y-auto">
                        <button
                          onClick={() => {
                            setActiveTab('live');
                            setShowWorkersDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition"
                          data-testid="live-workforce-btn"
                        >
                          <div className="p-2 bg-emerald-100 rounded-lg">
                            <Activity className="w-4 h-4 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">Live Workforce</p>
                            <p className="text-xs text-slate-500">Real-time control tower</p>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            setActiveTab('forecast');
                            setShowWorkersDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition"
                          data-testid="forecast-btn"
                        >
                          <div className="p-2 bg-indigo-100 rounded-lg">
                            <TrendingUp className="w-4 h-4 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">Predictive Forecast</p>
                            <p className="text-xs text-slate-500">AI-driven seasonal demand</p>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            setActiveTab('locations');
                            setShowWorkersDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition"
                          data-testid="locations-btn"
                        >
                          <div className="p-2 bg-violet-100 rounded-lg">
                            <MapPin className="w-4 h-4 text-violet-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">Locations</p>
                            <p className="text-xs text-slate-500">Per-site stats & geofence</p>
                          </div>
                        </button>
                        <div className="border-t border-slate-100 my-1" />
                        <button
                          onClick={() => {
                            // Auto-select first active job for matching, or open workers tab
                            const activeJob = jobs.find(j => j.status === 'active');
                            if (activeJob) {
                              setSelectedJob(activeJob);
                              setShowWorkerMatching(true);
                            } else {
                              toast.info('No active jobs to match. Post a job first.');
                            }
                            setShowWorkersDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition"
                          data-testid="workers-matching-btn"
                        >
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Search className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">Worker Matching</p>
                            <p className="text-xs text-slate-500">Find workers for jobs</p>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            setShowRelationshipManagement(true);
                            setShowWorkersDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition"
                          data-testid="preferred-workers-btn"
                        >
                          <div className="p-2 bg-pink-100 rounded-lg">
                            <Heart className="w-4 h-4 text-pink-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">Preferred Workers</p>
                            <p className="text-xs text-slate-500">Your favorite workers</p>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            setActiveTab('workers');
                            setShowWorkersDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition"
                          data-testid="workers-management-btn"
                        >
                          <div className="p-2 bg-emerald-100 rounded-lg">
                            <Users className="w-4 h-4 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">Workers Management</p>
                            <p className="text-xs text-slate-500">Manage all workers</p>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            setActiveTab('user-mgmt');
                            setShowWorkersDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition"
                          data-testid="user-mgmt-btn"
                        >
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <UserPlus className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">User Management</p>
                            <p className="text-xs text-slate-500">Add workers · reset passwords</p>
                          </div>
                        </button>
                        {(user?.role === 'agent' || user?.role === 'employer') && (
                          <button
                            onClick={() => {
                              setShowBulkWorkerUpload(true);
                              setShowWorkersDropdown(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition"
                            data-testid="bulk-worker-upload-btn"
                          >
                            <div className="p-2 bg-cyan-100 rounded-lg">
                              <Upload className="w-4 h-4 text-cyan-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">Bulk Upload</p>
                              <p className="text-xs text-slate-500">CSV / Excel worker import</p>
                            </div>
                          </button>
                        )}
                        {user?.role === 'agent' && (
                          <button
                            onClick={() => {
                              setActiveTab('claim-shifts');
                              setShowWorkersDropdown(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition"
                            data-testid="agent-claim-shifts-btn"
                          >
                            <div className="p-2 bg-teal-100 rounded-lg">
                              <Sparkles className="w-4 h-4 text-teal-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">Claim &amp; Assign Shifts</p>
                              <p className="text-xs text-slate-500">Claim shifts from other employers</p>
                            </div>
                          </button>
                        )}
                        <div className="border-t border-slate-100 my-1" />
                        <button
                          onClick={() => {
                            setActiveTab('accepted-jobs');
                            setShowWorkersDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition"
                          data-testid="accepted-jobs-btn"
                        >
                          <div className="p-2 bg-indigo-100 rounded-lg">
                            <CheckCircle className="w-4 h-4 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">Accepted Jobs</p>
                            <p className="text-xs text-slate-500">Jobs accepted by workers</p>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            setActiveTab('sent-invites');
                            setShowWorkersDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition"
                          data-testid="sent-invites-btn"
                        >
                          <div className="p-2 bg-violet-100 rounded-lg">
                            <Mail className="w-4 h-4 text-violet-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">Sent Invitations</p>
                            <p className="text-xs text-slate-500">Track invites & resend reminders</p>
                          </div>
                        </button>
                        <div className="border-t border-slate-100 my-1" />
                        <button
                          onClick={() => {
                            setActiveTab('schedule');
                            setShowWorkersDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition"
                          data-testid="schedule-dropdown-btn"
                        >
                          <div className="p-2 bg-sky-100 rounded-lg">
                            <Calendar className="w-4 h-4 text-sky-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">Schedule</p>
                            <p className="text-xs text-slate-500">Calendar & shift planning</p>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            setActiveTab('task-reviews');
                            setShowWorkersDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition"
                          data-testid="tasks-dropdown-btn"
                        >
                          <div className="p-2 bg-violet-100 rounded-lg">
                            <ClipboardList className="w-4 h-4 text-violet-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">Tasks</p>
                            <p className="text-xs text-slate-500">Review shift tasks</p>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                      activeTab === tab.id
                        ? 'text-white shadow-lg shadow-indigo-500/40'
                        : 'text-white/75 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid={`employer-nav-${tab.id}`}
                    aria-current={activeTab === tab.id ? 'page' : undefined}
                  >
                    {activeTab === tab.id && (
                      <span className={user?.role === 'agent' ? 'nav-active-pill-agent' : 'nav-active-pill'} aria-hidden="true" />
                    )}
                    <span className="relative flex items-center gap-1.5">
                      {tab.icon}
                      {tab.label}
                      {tab.id === 'comms' && unreadMessages > 0 && (
                        <span className="bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center" data-testid="employer-tab-unread-badge">
                          {unreadMessages > 9 ? '9+' : unreadMessages}
                        </span>
                      )}
                    </span>
                  </button>
                )
              ))}
            </nav>

        </div>
      </header>

      {/* Top Up Modal */}
      {showTopUpForm && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900">Top Up Wallet</h3>
              <button onClick={() => setShowTopUpForm(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <form onSubmit={handleTopUp}>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate-700 mb-2">Amount ($)</label>
                <input
                  type="number"
                  value={topUpAmount}
                  onChange={(e) => setTopUpAmount(e.target.value)}
                  className="w-full h-12 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all"
                  placeholder="e.g., 500"
                  required
                  min="1"
                  step="0.01"
                  data-testid="topup-amount-input"
                />
                <div className="flex gap-2 mt-3">
                  {[100, 250, 500, 1000].map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      onClick={() => setTopUpAmount(amount.toString())}
                      className="flex-1 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowTopUpForm(false)}
                  className="flex-1 px-4 py-3 border border-slate-200 rounded-xl font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 shadow-lg shadow-blue-600/25 transition-all"
                  data-testid="topup-submit-button"
                >
                  Add Funds
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Job Form Modal - Enhanced with AI Features */}
      <EmployerJobFormModal ctx={{ addJobTask, aiJobLoading, basicCanvasRef, basicSignatureMode, basicTypedSignature, clearBasicCanvas, drawBasic, generateJobDescription, handleCreateJob, handleJobMedicationToggle, jobForm, newJobTask, removeJobTask, setBasicSignatureMode, setBasicTypedSignature, setJobForm, setNewJobTask, setShowJobForm, showJobForm, startBasicDrawing, stopBasicDrawing, suggestJobTasks }} />

      {/* Main Content */}
      <main className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Verification Banner - Show if not verified */}
        {(!verificationStatus || verificationStatus?.status !== 'verified') && (
          <div className="mb-8 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <Shield className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-amber-900">Complete Your Business Verification</h3>
                  <p className="text-sm text-amber-700">
                    Verify your business to unlock all features and start posting jobs.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowOrganizationSetup(true)}
                className="px-5 py-2.5 bg-amber-600 text-white rounded-xl font-semibold hover:bg-amber-700 transition flex items-center gap-2"
                data-testid="complete-verification-btn"
              >
                <Building2 className="w-5 h-5" />
                Complete Setup
              </button>
            </div>
          </div>
        )}

        {/* Quick Actions Bar */}
        <div className="mb-8 bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowEnhancedJobPosting(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition"
              data-testid="enhanced-post-job-btn"
            >
              <Sparkles className="w-5 h-5" />
              Post Job with AI
            </button>
            <button
              onClick={() => setShowGroupHomesManagement(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition"
              data-testid="group-homes-management-btn"
            >
              <Home className="w-5 h-5" />
              Admin Dashboard
            </button>
            <button
              onClick={() => setShowRelationshipManagement(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl font-semibold hover:shadow-lg transition"
              data-testid="preferred-workers-btn"
            >
              <Heart className="w-5 h-5" />
              Preferred Workers
            </button>
            <button
              onClick={() => setActiveTab('locations')}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-semibold hover:shadow-lg transition"
              data-testid="locations-quick-action-btn"
            >
              <MapPin className="w-5 h-5" />
              Locations
            </button>
            <button
              onClick={() => setActiveTab('forecast')}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-500 to-violet-600 text-white rounded-xl font-semibold hover:shadow-lg transition"
              data-testid="forecast-quick-action-btn"
            >
              <TrendingUp className="w-5 h-5" />
              AI Forecast
            </button>
            <button
              onClick={() => setShowOrganizationSetup(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition"
              data-testid="organization-setup-btn"
            >
              <Building2 className="w-5 h-5" />
              Organization
            </button>
          </div>
        </div>

        <Suspense fallback={<div className="flex justify-center py-16"><span className="w-6 h-6 border-2 border-slate-300 border-t-indigo-600 rounded-full animate-spin" /></div>}>
        {activeTab === 'locations' && <div data-testid="locations-tab">{user?.role === 'agent' ? <AgentLocationsTab /> : <LocationsPanel />}</div>}
        {activeTab === 'forecast' && <div data-testid="forecast-tab"><PredictiveForecast /></div>}
        {activeTab === 'analytics' && <div data-testid="analytics-tab"><EmployerAnalyticsDashboard /></div>}
        {activeTab === 'schedule' && <div data-testid="schedule-tab"><ScheduleCalendar /></div>}
        {activeTab === 'live' && <div data-testid="live-tab"><LiveWorkforceDashboard /></div>}
        {activeTab === 'crm' && <div data-testid="crm-tab"><CRMPanel /></div>}
        {activeTab === 'comms' && (
          <div className="space-y-4" data-testid="comms-tab">
            <div className="flex gap-2 bg-white rounded-2xl border border-slate-200 p-1 shadow-sm w-fit">
              <button
                onClick={() => setCommsView('messages')}
                className={`px-4 py-1.5 rounded-xl text-sm font-bold transition ${
                  commsView !== 'broadcasts'
                    ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="comms-tab-messages"
              >
                Messages
                {unreadMessages > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </button>
              <button
                onClick={() => setCommsView('broadcasts')}
                className={`px-4 py-1.5 rounded-xl text-sm font-bold transition ${
                  commsView === 'broadcasts'
                    ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="comms-tab-broadcasts"
              >
                Broadcasts
              </button>
            </div>
            {commsView === 'broadcasts'
              ? <CommunicationHub />
              : <EmployerInbox currentUserId={user?._id} />}
          </div>
        )}
        {activeTab === 'my-jobs' && (
          <EmployerMyJobsSection ctx={{
            user, jobs, activeJobs, completedJobs, totalSpent, recentlyArchived,
            showRecentlyArchived, setShowRecentlyArchived, restoreOneRecentlyArchived,
            restoreAllRecentlyArchived, restoringRecent, myJobsView, setMyJobsView,
            drafts, draftsLoading, loadDrafts, loadJobs, handleDeleteDraft,
            showArchivedJobs, setShowArchivedJobs, jobsSearchQuery, setJobsSearchQuery,
            loading, setShowEnhancedJobPosting, selectedJobIds, toggleSelectAllJobs,
            toggleJobSelected, clearJobSelection, jobActionBusy,
            setJobActionError, setJobActionModal, bulkRestoreJobs, getJobStatusBadge,
            setSelectedJob, setShowWorkerMatching, setShowProjectTracker,
            setShowCompletionApproval, handleReleasePayment, restoreJob,
            AgentPortalPanel, EmployerOrgPanel, ShiftToolsButtons,
          }} />
        )}

        {/* Task Reviews Tab (extracted Feb 25, 2026 — logic + fetch stay here) */}
        {activeTab === 'task-reviews' && (
          <EmployerTaskReviewsSection
            taskReviews={taskReviews}
            taskReviewsLoading={taskReviewsLoading}
            reviewFilter={reviewFilter}
            setReviewFilter={setReviewFilter}
            loadTaskReviews={loadTaskReviews}
            handleApproveTask={handleApproveTask}
            handleRejectTask={handleRejectTask}
          />
        )}

        {activeTab === 'bulk-assign' && <BulkAssignPanel />}

        {activeTab === 'workers' && (
          <EmployerWorkersOverviewSection
            jobs={jobs}
            setSelectedJob={setSelectedJob}
            setShowWorkerMatching={setShowWorkerMatching}
            setShowRelationshipManagement={setShowRelationshipManagement}
          />
        )}

        {activeTab === 'payments' && (
          <PaymentsFinancial wallet={wallet} onTopUp={() => setShowTopUp(true)} role={user?.role} onWalletRefresh={loadWallet} />
        )}

        {activeTab === 'billing' && (
          <BillingDashboard />
        )}

        {/* Help & Training Tab */}
        {activeTab === 'help-training' && (
          <HelpTraining role={user?.role === 'agent' ? 'agent' : 'employer'} />
        )}

        {/* Accepted Jobs Tab */}
        {activeTab === 'accepted-jobs' && (
          <AcceptedJobsPanel />
        )}

        {/* Sent Invitations Tab */}
        {activeTab === 'sent-invites' && (
          <SentInvitations />
        )}

        {/* Compliance Tab */}
        {activeTab === 'compliance' && (
          <CompliancePanel />
        )}

        {/* Background Checks Tab (employer: connected workers, agent: roster) */}
        {activeTab === 'background-checks' && (
          <BackgroundChecks role={user?.role} />
        )}

        {/* Timesheets Tab */}
        {activeTab === 'timesheets' && (
          <TimesheetsPanel />
        )}

        {/* Shift Swaps Tab */}
        {activeTab === 'shift-swaps' && (
          <ShiftSwapManagement />
        )}

        {/* Staff Development Tab */}
        {activeTab === 'hris' && (
          <OrgHrisConsole />
        )}

        {activeTab === 'user-mgmt' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">User Management</h2>
                <p className="text-sm text-slate-500 mt-1">Add workers individually or import a roster from CSV / Excel.</p>
              </div>
              <button
                onClick={() => setShowBulkWorkerUpload(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl font-semibold shadow hover:shadow-lg hover:from-cyan-700 hover:to-blue-700 transition"
                data-testid="user-mgmt-bulk-upload-cta"
              >
                <Upload className="w-4 h-4" />
                Bulk Upload Workers
                <span className="ml-1 text-xs bg-white/20 px-2 py-0.5 rounded-full">CSV · Excel</span>
              </button>
            </div>
            <UserManagementPanel mode="employer" currentUser={user} />
          </div>
        )}

        {activeTab === 'claim-shifts' && user?.role === 'agent' && (
          <ClaimShifts />
        )}

        {activeTab === 'contracts' && (
          <AgencyContracts role={user?.role === 'agent' ? 'agent' : 'employer'} />
        )}

        {activeTab === 'overview' && (
          <EmployerProfileOverview ctx={{
            user, wallet, verificationStatus, setActiveTab, setShowOrganizationSetup,
            onPhotoChange: handleProfilePhotoUpload, uploadingPhoto,
          }} />
        )}
        {activeTab === 'settings' && (
          <EmployerSettingsSection ctx={{
            user, wallet, verificationStatus, setShowOrganizationSetup,
            ReportRecipientsManager, NotificationPrefsCard, SensitiveSection,
            SecurityCenter, SupportHistoryPanel, LocationsManager,
            AutomationRulesPanel, WorkforcePoliciesCard, GeofenceSettings,
            AutoPayrollSettings,
          }} />
        )}
        </Suspense>
      </main>

      {/* Messaging Inbox */}
      {showMessaging && !messagingRecipient && (
        <MessagingInbox
          onClose={() => setShowMessaging(false)}
        />
      )}

      {/* Real-Time Chat Panel */}
      {showMessaging && messagingRecipient && (
        <RealTimeChat
          recipientId={messagingRecipient.id}
          recipientName={messagingRecipient.name}
          jobId={messagingRecipient.jobId}
          onClose={() => {
            setShowMessaging(false);
            setMessagingRecipient(null);
          }}
        />
      )}

      {/* Employer Portal Modals (Phase 1-7) */}
      
      {/* Phase 1: Organization / Agency Setup — open the role-appropriate
          wizard so an agent doesn't accidentally see the Employer form. */}
      {showOrganizationSetup && (
        user?.role === 'agent' ? (
          <AgencySetup
            onClose={() => setShowOrganizationSetup(false)}
            onVerificationComplete={() => {
              setShowOrganizationSetup(false);
              loadVerificationStatus();
              toast.success('Agency setup complete!');
            }}
          />
        ) : (
          <OrganizationSetup
            onClose={() => setShowOrganizationSetup(false)}
            onVerificationComplete={() => {
              setShowOrganizationSetup(false);
              loadVerificationStatus();
              toast.success('Organization setup complete!');
            }}
          />
        )
      )}

      {/* Phase 2: Enhanced Job Posting with AI */}
      {showEnhancedJobPosting && (
        <EnhancedJobPosting
          onClose={() => setShowEnhancedJobPosting(false)}
          onJobCreated={(job) => {
            loadJobs();
            if (myJobsView === 'drafts') loadDrafts();
            toast.success('Job posted successfully!');
          }}
          editJob={typeof showEnhancedJobPosting === 'object' ? showEnhancedJobPosting : null}
          onDraftSaved={() => {
            setActiveTab('my-jobs');
            setMyJobsView('drafts');
            loadDrafts();
          }}
        />
      )}

      {/* Phase 3: Worker Matching */}
      {showWorkerMatching && selectedJob && (
        <WorkerMatching
          job={selectedJob}
          onClose={() => {
            setShowWorkerMatching(false);
            setSelectedJob(null);
          }}
          onWorkerSelected={(workerId) => {
            loadJobs();
          }}
        />
      )}

      {/* Phase 4: Project Tracker */}
      {showProjectTracker && selectedJob && (
        <ProjectTracker
          job={selectedJob}
          onClose={() => {
            setShowProjectTracker(false);
            setSelectedJob(null);
          }}
          onStatusChange={() => {
            loadJobs();
          }}
        />
      )}

      {/* Phase 5: Completion & Approval */}
      {showCompletionApproval && selectedJob && (
        <CompletionApproval
          job={selectedJob}
          onClose={() => {
            setShowCompletionApproval(false);
            setSelectedJob(null);
          }}
          onApproved={(approval) => {
            loadJobs();
            loadWallet();
          }}
        />
      )}

      {/* Phase 6: Payment Management */}
      {showPaymentManagement && (
        <PaymentManagement
          onClose={() => setShowPaymentManagement(false)}
          onOpenAIJobPosting={() => setShowEnhancedJobPosting(true)}
          onOpenAdminDashboard={() => setShowGroupHomesManagement(true)}
          onOpenPreferredWorkers={() => setActiveTab('workers')}
        />
      )}

      {/* Phase 7: Relationship Management */}
      {showRelationshipManagement && (
        <RelationshipManagement
          onClose={() => setShowRelationshipManagement(false)}
          onHireWorker={(workerId) => {
            setShowRelationshipManagement(false);
            setShowEnhancedJobPosting(true);
          }}
        />
      )}

      {/* Phase 8: Group Homes Enterprise Management */}
      {showGroupHomesManagement && (
        <GroupHomesManagement
          onClose={() => setShowGroupHomesManagement(false)}
        />
      )}

      {/* Job Archive / Delete password confirmation */}
      <PasswordConfirmModal
        open={!!jobActionModal}
        onClose={() => { if (!jobActionBusy) { setJobActionModal(null); setJobActionError(''); } }}
        onConfirm={submitJobAction}
        busy={jobActionBusy}
        error={jobActionError}
        requireReason
        tone={(jobActionModal?.mode === 'delete' || jobActionModal?.mode === 'bulk-delete') ? 'danger' : 'warning'}
        title={
          jobActionModal?.mode === 'bulk-delete'
            ? `Permanently delete ${jobActionModal?.jobIds?.length || 0} job${(jobActionModal?.jobIds?.length || 0) !== 1 ? 's' : ''}?`
          : jobActionModal?.mode === 'bulk-archive'
            ? `Archive ${jobActionModal?.jobIds?.length || 0} job${(jobActionModal?.jobIds?.length || 0) !== 1 ? 's' : ''}?`
          : jobActionModal?.mode === 'delete'
            ? `Permanently delete "${jobActionModal?.job?.title || 'job'}"?`
            : `Archive "${jobActionModal?.job?.title || 'job'}"?`
        }
        description={
          (jobActionModal?.mode === 'delete' || jobActionModal?.mode === 'bulk-delete')
            ? 'This will erase the selected job(s) and ALL related applications, invites, attendance and chat data. This cannot be undone.'
            : 'Selected job(s) will be hidden from workers and pending applications/invites will be cancelled. You can restore them later.'
        }
        confirmLabel={
          jobActionModal?.mode === 'bulk-delete' ? `Delete ${jobActionModal?.jobIds?.length || 0} Permanently`
          : jobActionModal?.mode === 'bulk-archive' ? `Archive ${jobActionModal?.jobIds?.length || 0} Job${(jobActionModal?.jobIds?.length || 0) !== 1 ? 's' : ''}`
          : jobActionModal?.mode === 'delete' ? 'Delete Permanently'
          : 'Archive Job'
        }
      />

      {/* Top Up Wallet Modal */}
      <EmployerTopUpModal ctx={{ handleTopUp, setShowTopUp, setTopUpAmount, showTopUp, topUpAmount }} />

      {/* Shift Requests Modal */}
      <EmployerShiftRequestsModal ctx={{ handleApproveShiftRequest, handleRejectShiftRequest, loadShiftRequests, setShowShiftRequests, shiftRequests, shiftRequestsLoading, showShiftRequests }} />

      {/* AI Support Modal */}
      {showAiSupport && (
        <EmployerAiSupportChat 
          onClose={() => setShowAiSupport(false)}
        />
      )}

      {/* Help & Support Modal */}
      <EmployerHelpSupportModal ctx={{ helpView, setHelpView, setShowAiSupport, setShowHelpSupport, showHelpSupport }} />

      {/* Time Edit Requests Modal */}
      <EmployerTimeEditRequestsModal ctx={{ handleApproveTimeEdit, handleRejectTimeEdit, loadTimeEditRequests, setShowTimeEditRequests, showTimeEditRequests, timeEditRequests, timeEditRequestsLoading }} />
      {showBulkWorkerUpload && (
        <BulkWorkerUpload
          onClose={() => setShowBulkWorkerUpload(false)}
          onSuccess={() => {
            toast.success('Bulk worker upload complete.');
          }}
        />
      )}
    </div>
  );
}

